import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Tooltip,
  Card,
} from '@mui/material';
import {
  Timer as TimerIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CheckCircle as CompletedIcon,
  PlayCircleOutline as ActiveIcon,
  PauseCircleOutline as InactiveIcon,
} from '@mui/icons-material';
import { getProductSessions, getProductSessionById, updateProductSession, deleteProductSession } from '../../api/adminApi';
import { getAllExams } from '../../api/examApi';
import { useToast } from '../../contexts/ToastContext';
import {
  PageHeader,
  ModernButton,
  ModernDataGrid,
} from '../../components/common';

const ProductSessionsManagement = () => {
  const { showToast } = useToast();
  const [sessions, setSessions] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [completedFilter, setCompletedFilter] = useState('');
  const [examFilter, setExamFilter] = useState('');

  // Dialogs
  const [viewDialog, setViewDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: Number(page), limit: Number(pageSize) };
      if (statusFilter !== '') params.hasStarted = statusFilter;
      if (completedFilter !== '') params.isCompleted = completedFilter;
      if (examFilter !== '') params.productId = examFilter;

      const { data } = await getProductSessions(params);
      setSessions(data.sessions);
      setTotalPages(data.pagination.pages);
      setTotalCount(data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      showToast('Failed to fetch sessions', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, completedFilter, examFilter, showToast]);

  // Fetch exams for filter
  const fetchExams = useCallback(async () => {
    try {
      const { data } = await getAllExams();
      setExams(data);
    } catch (error) {
      console.error('Failed to fetch exams:', error);
      showToast('Failed to load exams', 'error');
    }
  }, [showToast]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Handlers
  const handleViewSession = async (session) => {
    try {
      const { data } = await getProductSessionById(session._id);
      setViewDialog(data);
    } catch (error) {
      showToast('Failed to load session details', 'error');
    }
  };

  const handleEditSession = (session) => {
    setEditForm({
      _id: session._id,
      hasStarted: session.hasStarted,
      isCompleted: session.isCompleted,
      startTime: session.startTime ? new Date(session.startTime).toISOString().slice(0, 16) : '',
      endTime: session.endTime ? new Date(session.endTime).toISOString().slice(0, 16) : '',
      examSessionTime: session.examSessionTime ? new Date(session.examSessionTime).toISOString().slice(0, 16) : '',
      lastHeartbeat: session.lastHeartbeat ? new Date(session.lastHeartbeat).toISOString().slice(0, 16) : '',
    });
    setEditDialog(true);
  };

  const handleSaveSession = async () => {
    try {
      const { _id, ...updateData } = editForm;
      if (updateData.startTime) updateData.startTime = new Date(updateData.startTime);
      if (updateData.endTime) updateData.endTime = new Date(updateData.endTime);
      if (updateData.examSessionTime) updateData.examSessionTime = new Date(updateData.examSessionTime);
      if (updateData.lastHeartbeat) updateData.lastHeartbeat = new Date(updateData.lastHeartbeat);

      await updateProductSession(_id, updateData);
      showToast('Session updated successfully', 'success');
      setEditDialog(null);
      fetchSessions();
    } catch (error) {
      showToast('Failed to update session', 'error');
    }
  };

  const handleDeleteSession = async () => {
    if (!deleteDialog) return;
    try {
      await deleteProductSession(deleteDialog._id);
      showToast('Session deleted successfully', 'success');
      setDeleteDialog(null);
      fetchSessions();
    } catch (error) {
      showToast('Failed to delete session', 'error');
    }
  };

  // Helper functions
  const formatDuration = (startTime, endTime) => {
    if (!startTime) return 'N/A';
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const durationMs = end - start;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const getSessionStatusIcon = (session) => {
    if (session.isCompleted) return <CompletedIcon sx={{ color: 'success.main' }} />;
    if (session.hasStarted) return <ActiveIcon sx={{ color: 'info.main' }} />;
    return <InactiveIcon sx={{ color: 'grey.500' }} />;
  };

  const getSessionStatus = (session) => {
    if (session.isCompleted) return { label: 'Completed', color: 'success' };
    if (session.hasStarted) return { label: 'Active', color: 'info' };
    return { label: 'Inactive', color: 'default' };
  };

  // Columns definition
  const columns = [
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      renderCell: (params) => params.row.userId ? (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.userId.firstName} {params.row.userId.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.userId.email}
          </Typography>
        </Box>
      ) : 'N/A',
    },
    {
      field: 'exam',
      headerName: 'Product',
      flex: 1,
      renderCell: (params) => {
        const registration = params.row.userProductRegistration;
        const product = registration?.productId;
        // Exam has 'name', Reading/Writing/Listening have 'label'
        const productName = product?.name || product?.label;
        const productType = registration?.productType;
        return product ? (
          <Box>
            <Typography variant="body2">
              {productName || 'N/A'}
            </Typography>
            {productType && (
              <Typography variant="caption" color="text.secondary">
                {productType}
              </Typography>
            )}
          </Box>
        ) : 'N/A';
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const status = getSessionStatus(params.row);
        return (
          <Chip
            icon={getSessionStatusIcon(params.row)}
            label={status.label}
            color={status.color}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 120,
      valueGetter: (params) => formatDuration(params.row.startTime, params.row.endTime),
    },
    {
      field: 'startTime',
      headerName: 'Started',
      width: 160,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : 'N/A',
    },
    {
      field: 'lastHeartbeat',
      headerName: 'Last Activity',
      width: 160,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : 'N/A',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewSession(params.row)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditSession(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => setDeleteDialog(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Product Sessions Management"
        subtitle="Monitor and manage all exam sessions"
        icon={<TimerIcon />}
        iconColor="primary.main"
      />

      {/* Filters */}
      <Card sx={{ mb: 3, p: 3, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Exam"
              value={examFilter}
              onChange={(e) => setExamFilter(e.target.value)}
            >
              <MenuItem value="">All Exams</MenuItem>
              {exams.map((exam) => (
                <MenuItem key={exam._id} value={exam._id}>
                  {exam.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Session Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Sessions</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Completion Status"
              value={completedFilter}
              onChange={(e) => setCompletedFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Completed</MenuItem>
              <MenuItem value="false">In Progress</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <ModernButton fullWidth variant="contained" onClick={fetchSessions}>
              Apply Filters
            </ModernButton>
          </Grid>
        </Grid>
      </Card>

      {/* Data Grid */}
      <ModernDataGrid
        rows={sessions}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
        pagination
        page={page - 1}
        onPageChange={(newPage) => setPage(newPage + 1)}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(1);
        }}
        rowsPerPageOptions={[20, 50, 100]}
        rowCount={totalCount}
        paginationMode="server"
      />

      {/* View Dialog */}
      {viewDialog && (
        <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="md" fullWidth>
          <DialogTitle>Session Details</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      User Information
                    </Typography>
                    <Typography variant="body1">
                      <strong>Name:</strong> {viewDialog.userId?.firstName} {viewDialog.userId?.lastName}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Email:</strong> {viewDialog.userId?.email}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Session Information
                    </Typography>
                    <Typography variant="body1">
                      <strong>Product:</strong> {viewDialog.userProductRegistration?.productId?.name || viewDialog.userProductRegistration?.productId?.label || 'N/A'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Product Type:</strong> {viewDialog.userProductRegistration?.productType || 'N/A'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Status:</strong> {viewDialog.isCompleted ? 'Completed' : viewDialog.hasStarted ? 'Active' : 'Inactive'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Duration:</strong> {formatDuration(viewDialog.startTime, viewDialog.endTime)}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Start Time:</strong> {viewDialog.startTime ? new Date(viewDialog.startTime).toLocaleString() : 'N/A'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>End Time:</strong> {viewDialog.endTime ? new Date(viewDialog.endTime).toLocaleString() : 'N/A'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Last Heartbeat:</strong> {viewDialog.lastHeartbeat ? new Date(viewDialog.lastHeartbeat).toLocaleString() : 'N/A'}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
              {viewDialog.userProductRegistration?.score && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Score
                      </Typography>
                      <Typography variant="body1">
                        <strong>Overall Score:</strong> {viewDialog.userProductRegistration.score.overAllScore || 'N/A'}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setViewDialog(null)}>Close</ModernButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Dialog */}
      {editDialog && (
        <Dialog open={editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Session</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Has Started"
                  value={editForm.hasStarted}
                  onChange={(e) => setEditForm({ ...editForm, hasStarted: e.target.value })}
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Is Completed"
                  value={editForm.isCompleted}
                  onChange={(e) => setEditForm({ ...editForm, isCompleted: e.target.value })}
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="datetime-local"
                  value={editForm.startTime}
                  onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="datetime-local"
                  value={editForm.endTime}
                  onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Exam Session Time"
                  type="datetime-local"
                  value={editForm.examSessionTime}
                  onChange={(e) => setEditForm({ ...editForm, examSessionTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Last Heartbeat"
                  type="datetime-local"
                  value={editForm.lastHeartbeat}
                  onChange={(e) => setEditForm({ ...editForm, lastHeartbeat: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setEditDialog(null)}>Cancel</ModernButton>
            <ModernButton variant="contained" onClick={handleSaveSession}>
              Save Changes
            </ModernButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Dialog */}
      {deleteDialog && (
        <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
          <DialogTitle>Delete Session?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this session? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setDeleteDialog(null)}>Cancel</ModernButton>
            <ModernButton variant="contained" color="error" onClick={handleDeleteSession}>
              Delete
            </ModernButton>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ProductSessionsManagement;
